/**
 * Cloud Operations — Deployments list.
 *
 * Read-only listing of every Prove7 cluster row. "+ New deployment" routes
 * to the 3-step wizard. Row click → detail page.
 *
 * Per design doc §5 (GET /deployments) + Mockup §"Deployments".
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listDeployments } from '../../services/cloudOperationsApi';
import {
  T, btn, btnPrimary, Banner, Th, Td,
  StatusPill, fmtDate, shortId,
} from './_ui';

export default function DeploymentsListPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  function reload() {
    setLoading(true);
    listDeployments({ size: 100 })
      .then((d) => { setItems(d?.items || []); setLoading(false); })
      .catch((e) => { setError(e?.message || String(e)); setLoading(false); });
  }
  useEffect(reload, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((d) => {
      if (filterStatus !== 'all' && d.status !== filterStatus) return false;
      if (q && !(d.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, filterStatus]);

  return (
    <div>
      <ActionsBar onNew={() => nav('/cloud-operations/deployments/new')} />
      <FilterBar
        search={search} onSearch={setSearch}
        status={filterStatus} onStatus={setFilterStatus}
        total={items.length} shown={filtered.length}
      />
      {error   && <Banner color={T.danger} bg={T.dangerBg}>Could not load: {error}</Banner>}
      {loading && <Banner color={T.info}   bg={T.infoBg}>Loading…</Banner>}
      {!loading && (filtered.length === 0 ? <Empty hasItems={items.length > 0} /> : <Table rows={filtered} onOpen={(r) => nav(`/cloud-operations/deployments/${r.id}`)} />)}
    </div>
  );
}

function ActionsBar({ onNew }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
      <div style={{ color: T.text2, fontSize: 13 }}>
        Every Prove7 cluster across both deployment modes and all supported clouds.
      </div>
      <div style={{ flex: 1 }} />
      <button onClick={onNew} style={btnPrimary}>+ New deployment</button>
    </div>
  );
}

function FilterBar({ search, onSearch, status, onStatus, total, shown }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
      <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search by deployment name…"
             style={{ flex: 1, minWidth: 220, padding: '7px 12px', border: `1px solid ${T.stroke}`, borderRadius: 4, fontSize: 13 }} />
      <select value={status} onChange={(e) => onStatus(e.target.value)} style={selSm}>
        <option value="all">All statuses</option>
        <option value="pending">Pending</option>
        <option value="provisioning">Provisioning</option>
        <option value="healthy">Healthy</option>
        <option value="drifted">Drifted</option>
        <option value="degraded">Degraded</option>
        <option value="failed">Failed</option>
        <option value="upgrading">Upgrading</option>
        <option value="decommissioning">Decommissioning</option>
        <option value="decommissioned">Decommissioned</option>
      </select>
      <div style={{ color: T.text2, fontSize: 12 }}>{shown} of {total}</div>
    </div>
  );
}

function Empty({ hasItems }) {
  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6,
      padding: 40, textAlign: 'center', color: T.text3, fontStyle: 'italic',
    }}>
      {hasItems
        ? 'No deployments match the current filters.'
        : 'No deployments yet. Click "+ New deployment" to author one against a Deployment Target.'}
    </div>
  );
}

function Table({ rows, onOpen }) {
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.bgTint }}>
            <Th>Deployment</Th>
            <Th>Target</Th>
            <Th>Helm release</Th>
            <Th>Version</Th>
            <Th>Status</Th>
            <Th>Created</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.id} style={{ borderTop: `1px solid ${T.stroke2}`, cursor: 'pointer' }}
                onClick={() => onOpen(d)}>
              <Td>
                <div style={{ fontWeight: 600, color: T.ink }}>{d.name}</div>
                <div style={{ color: T.text3, fontSize: 11 }}><code>{shortId(d.id)}</code></div>
              </Td>
              <Td><code style={{ fontSize: 11 }}>{shortId(d.target_id)}</code></Td>
              <Td><code style={{ fontSize: 11 }}>{d.helm_release_name}</code>
                  <div style={{ color: T.text3, fontSize: 11 }}>rev {d.helm_release_revision ?? '—'}</div></Td>
              <Td><code style={{ fontSize: 11 }}>{d.platform_version || '—'}</code></Td>
              <Td><StatusPill status={d.status} /></Td>
              <Td><span style={{ color: T.text2, fontSize: 11 }}>{fmtDate(d.created_at)}</span></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const selSm = {
  padding: '7px 10px', border: `1px solid ${T.stroke}`,
  borderRadius: 4, fontSize: 13, background: '#fff',
};
